import { StatsigClient, useStatsigClient } from "@statsig/react-bindings";
import { StatsigTriggeredSessionReplayPlugin } from "@statsig/session-replay";
import { StatsigAutoCapturePlugin } from "@statsig/web-analytics";
import { captureException } from "@sentry/react";
import { Cookies } from "react-cookie";
import { useEffect, useRef } from "react";

const sdkKey = import.meta.env.VITE_STATSIG_SDK_KEY;

// Block drops the element entirely (personal data, QR codes, iframes).
export const REPLAY_BLOCK_CLASS = "sr-block";

// The landing page sets `uid` on the parent domain, so a visitor buckets the same
// there and here. Falls back to the SDK's own stableID when they never visited landing.
const stableID = new Cookies().get<string | undefined>("uid");

export const statsigClient = sdkKey
  ? new StatsigClient(
      sdkKey,
      {
        // The SDK leaves it empty, and Statsig derives browser/OS targeting from it.
        userAgent: globalThis.navigator?.userAgent ?? "",
        ...(stableID && { customIDs: { stableID } }),
      },
      {
        environment: { tier: import.meta.env.MODE },
        plugins: [
          new StatsigTriggeredSessionReplayPlugin({
            autoStartRecording: true,
            keepRollingWindow: true,
            rrwebConfig: { blockClass: REPLAY_BLOCK_CLASS },
          }),
          new StatsigAutoCapturePlugin(),
        ],
      },
    )
  : null;

statsigClient?.initializeAsync().catch(captureException);

// customerUuid is the identity-service id the app also sends as its userID, so a customer buckets the same way in both.
// Async, not sync: updateUserSync only reads the cache, so a userID rule missed the first visit.
export const setStatsigUserId = async (customerUuid?: string | null) => {
  if (!statsigClient || !customerUuid) return;

  const { user } = statsigClient.getContext();
  if (user.userID === customerUuid) return;

  await statsigClient.updateUserAsync({ ...user, userID: customerUuid });
};

const POA_GATE = "enable_pension_app_poa_flow";

// First loaded value wins: a later user switch reloads every gate mid flow.
// Read only once ready: a read during init logs an exposure Statsig flags as uninitialized.
const useSettledPoaGate = (when: boolean) => {
  const { isLoading } = useStatsigClient();
  const settled = useRef<boolean | undefined>(undefined);

  if (when && settled.current === undefined) {
    if (!statsigClient) settled.current = false;
    else if (!isLoading) settled.current = statsigClient.checkGate(POA_GATE);
  }

  return settled.current;
};

/** The anonymous visitor's value. It only picks the auth mode: whether a lead is created. */
export const useVisitorPoaGate = () => useSettledPoaGate(true);

/** Read again once auth has set the Statsig user, so rules on userID pick the flow. */
export const useEnablePoaGate = (userKnown: boolean) => {
  const enabled = useSettledPoaGate(userKnown);
  return { enabled: enabled ?? false, ready: enabled !== undefined };
};

// The console starts a recording on this gate's exposure, and exposures only count once
// values are loaded, so it is checked after init on every landing.
export const useSessionReplayGate = () => {
  const { isLoading } = useStatsigClient();

  useEffect(() => {
    if (!isLoading) statsigClient?.checkGate("pension_session_replay");
  }, [isLoading]);
};

// Autocapture records clicks but not which next step a customer was offered, and
// the CTA funnel needs both halves. Never pass personal data as metadata.
export const trackEvent = (
  eventName: string,
  metadata?: Record<string, string>,
) => {
  try {
    statsigClient?.logEvent({ eventName, metadata });
  } catch (error) {
    captureException(error);
  }
};

// The `step` metadata of the app POA funnel events (PEN-168), in screen order.
export const APP_POA_STEP = {
  intro: "1",
  dataRetrieval: "2",
  contactDetails: "3",
  poaInfo: "4",
  duration: "5",
  sign: "6",
} as const;
